import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from '../../contexts/ThemeContext';
import type { CustomerWithRelations, UpdateCustomerDto } from '../../types/customer360';
import { X } from 'lucide-react';

const CUSTOMER_TYPES: Array<{ value: 'individual' | 'business'; label: string }> = [
  { value: 'individual', label: 'Individual' },
  { value: 'business', label: 'Business' }
];

const LIFECYCLE_STAGES: Array<{ value: UpdateCustomerDto['lifecycle_stage']; label: string }> = [
  { value: 'lead', label: 'Lead' },
  { value: 'prospect', label: 'Prospect' },
  { value: 'customer', label: 'Customer' },
  { value: 'loyal', label: 'Loyal' },
  { value: 'at_risk', label: 'At Risk' },
  { value: 'churned', label: 'Churned' }
];

const LANGUAGE_OPTIONS = ['en', 'es', 'fr', 'de', 'ja', 'zh', 'pt', 'hi'];

interface CustomerProfileModalProps {
  isOpen: boolean;
  customer: CustomerWithRelations | null;
  onClose: () => void;
  onSave: (data: UpdateCustomerDto) => Promise<void>;
}

interface FormState {
  full_name: string;
  email?: string;
  phone: string;
  company_name?: string;
  job_title?: string;
  customer_type: 'individual' | 'business';
  lifecycle_stage: UpdateCustomerDto['lifecycle_stage'];
  health_score: number;
  preferred_language?: string;
  timezone?: string;
  source?: string;
  avatar_url?: string;
  notes?: string;
  is_vip: boolean;
  last_contact_date?: string;
  next_follow_up_date?: string;
  external_ref?: string;
}

const toDateInputValue = (value?: string) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().split('T')[0];
};

export default function CustomerProfileModal({
  isOpen,
  customer,
  onClose,
  onSave
}: CustomerProfileModalProps) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const initialFormState = useMemo<FormState>(() => ({
    full_name: customer?.full_name || '',
    email: customer?.email || '',
    phone: customer?.phone || '',
    company_name: customer?.company_name || '',
    job_title: customer?.job_title || '',
    customer_type: customer?.customer_type || 'individual',
    lifecycle_stage: customer?.lifecycle_stage || 'customer',
    health_score: customer?.health_score ?? 70,
    preferred_language: customer?.preferred_language || 'en',
    timezone: customer?.timezone || '',
    source: customer?.source || '',
    avatar_url: customer?.avatar_url || '',
    notes: customer?.notes || '',
    is_vip: customer?.is_vip || false,
    last_contact_date: toDateInputValue(customer?.last_contact_date),
    next_follow_up_date: toDateInputValue(customer?.next_follow_up_date),
    external_ref: customer?.external_ref || ''
  }), [customer]);

  const [formState, setFormState] = useState<FormState>(initialFormState);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setFormState(initialFormState);
      setErrors({});
    }
  }, [initialFormState, isOpen]);

  if (!isOpen || !customer) {
    return null;
  }

  const handleChange = <K extends keyof FormState>(field: K, value: FormState[K]) => {
    setFormState((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const validateForm = () => {
    const nextErrors: Record<string, string> = {};

    if (!formState.full_name.trim()) {
      nextErrors.full_name = 'Full name is required';
    }

    if (!formState.phone.trim()) {
      nextErrors.phone = 'Phone number is required';
    }

    if (formState.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formState.email)) {
      nextErrors.email = 'Invalid email format';
    }

    if (Number.isNaN(Number(formState.health_score)) || formState.health_score < 0 || formState.health_score > 100) {
      nextErrors.health_score = 'Health score must be between 0 and 100';
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!validateForm()) {
      return;
    }

    const payload: UpdateCustomerDto = {
      full_name: formState.full_name.trim(),
      phone: formState.phone.trim(),
      email: formState.email?.trim() || undefined,
      company_name: formState.company_name?.trim() || undefined,
      job_title: formState.job_title?.trim() || undefined,
      customer_type: formState.customer_type,
      lifecycle_stage: formState.lifecycle_stage,
      preferred_language: formState.preferred_language || undefined,
      timezone: formState.timezone?.trim() || undefined,
      source: formState.source?.trim() || undefined,
      avatar_url: formState.avatar_url?.trim() || undefined,
      notes: formState.notes?.trim() || undefined,
      is_vip: formState.is_vip,
      external_ref: formState.external_ref?.trim() || undefined,
      health_score: Number(formState.health_score),
      last_contact_date: formState.last_contact_date ? new Date(formState.last_contact_date).toISOString() : undefined,
      next_follow_up_date: formState.next_follow_up_date ? new Date(formState.next_follow_up_date).toISOString() : undefined
    };

    setIsSubmitting(true);
    try {
      await onSave(payload);
    } finally {
      setIsSubmitting(false);
    }
  };

  const modalContent = (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[9999]">
      <form
        onSubmit={handleSubmit}
        className={`rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] ${
          isDark ? 'bg-gray-800' : 'bg-white'
        } flex flex-col border ${isDark ? 'border-gray-700' : 'border-gray-200'}`}
      >
        <div
          className={`flex items-center justify-between px-6 py-4 border-b ${
            isDark ? 'border-gray-700' : 'border-gray-200'
          }`}
        >
          <h2 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Edit Customer Profile
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className={`rounded-md p-1 transition-colors ${
              isDark ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-500 hover:bg-gray-200'
            } ${isSubmitting ? 'cursor-not-allowed opacity-60' : ''}`}
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="text-sm font-medium">Full Name *</label>
              <input
                type="text"
                value={formState.full_name}
                onChange={(e) => handleChange('full_name', e.target.value)}
                className={`mt-1 w-full rounded-md border px-3 py-2 ${
                  errors.full_name ? 'border-red-500' : isDark ? 'border-gray-600 bg-gray-700' : 'border-gray-300'
                }`}
              />
              {errors.full_name && <p className="mt-1 text-sm text-red-500">{errors.full_name}</p>}
            </div>
            <div>
              <label className="text-sm font-medium">Email</label>
              <input
                type="email"
                value={formState.email || ''}
                onChange={(e) => handleChange('email', e.target.value)}
                className={`mt-1 w-full rounded-md border px-3 py-2 ${
                  errors.email ? 'border-red-500' : isDark ? 'border-gray-600 bg-gray-700' : 'border-gray-300'
                }`}
              />
              {errors.email && <p className="mt-1 text-sm text-red-500">{errors.email}</p>}
            </div>
            <div>
              <label className="text-sm font-medium">Phone *</label>
              <input
                type="text"
                value={formState.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
                className={`mt-1 w-full rounded-md border px-3 py-2 ${
                  errors.phone ? 'border-red-500' : isDark ? 'border-gray-600 bg-gray-700' : 'border-gray-300'
                }`}
              />
              {errors.phone && <p className="mt-1 text-sm text-red-500">{errors.phone}</p>}
            </div>
            <div>
              <label className="text-sm font-medium">Company</label>
              <input
                type="text"
                value={formState.company_name || ''}
                onChange={(e) => handleChange('company_name', e.target.value)}
                className={`mt-1 w-full rounded-md border px-3 py-2 ${isDark ? 'border-gray-600 bg-gray-700' : 'border-gray-300'}`}
              />
            </div>
            <div>
              <label className="text-sm font-medium">Job Title</label>
              <input
                type="text"
                value={formState.job_title || ''}
                onChange={(e) => handleChange('job_title', e.target.value)}
                className={`mt-1 w-full rounded-md border px-3 py-2 ${isDark ? 'border-gray-600 bg-gray-700' : 'border-gray-300'}`}
              />
            </div>
            <div>
              <label className="text-sm font-medium">Customer Type</label>
              <select
                value={formState.customer_type}
                onChange={(e) => handleChange('customer_type', e.target.value as FormState['customer_type'])}
                className={`mt-1 w-full rounded-md border px-3 py-2 ${isDark ? 'border-gray-600 bg-gray-700' : 'border-gray-300'}`}
              >
                {CUSTOMER_TYPES.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium">Lifecycle Stage</label>
              <select
                value={formState.lifecycle_stage || 'customer'}
                onChange={(e) => handleChange('lifecycle_stage', e.target.value as FormState['lifecycle_stage'])}
                className={`mt-1 w-full rounded-md border px-3 py-2 ${isDark ? 'border-gray-600 bg-gray-700' : 'border-gray-300'}`}
              >
                {LIFECYCLE_STAGES.map((stage) => (
                  <option key={stage.value} value={stage.value || ''}>
                    {stage.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium">Preferred Language</label>
              <select
                value={formState.preferred_language || ''}
                onChange={(e) => handleChange('preferred_language', e.target.value)}
                className={`mt-1 w-full rounded-md border px-3 py-2 ${isDark ? 'border-gray-600 bg-gray-700' : 'border-gray-300'}`}
              >
                <option value="">Select language</option>
                {LANGUAGE_OPTIONS.map((code) => (
                  <option key={code} value={code}>
                    {code.toUpperCase()}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium">Health Score</label>
              <input
                type="number"
                min={0}
                max={100}
                value={formState.health_score}
                onChange={(e) => handleChange('health_score', Number(e.target.value))}
                className={`mt-1 w-full rounded-md border px-3 py-2 ${
                  errors.health_score ? 'border-red-500' : isDark ? 'border-gray-600 bg-gray-700' : 'border-gray-300'
                }`}
              />
              {errors.health_score && <p className="mt-1 text-sm text-red-500">{errors.health_score}</p>}
            </div>
            <div>
              <label className="text-sm font-medium">Timezone</label>
              <input
                type="text"
                value={formState.timezone || ''}
                onChange={(e) => handleChange('timezone', e.target.value)}
                className={`mt-1 w-full rounded-md border px-3 py-2 ${isDark ? 'border-gray-600 bg-gray-700' : 'border-gray-300'}`}
                placeholder="e.g., America/Los_Angeles"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Source</label>
              <input
                type="text"
                value={formState.source || ''}
                onChange={(e) => handleChange('source', e.target.value)}
                className={`mt-1 w-full rounded-md border px-3 py-2 ${isDark ? 'border-gray-600 bg-gray-700' : 'border-gray-300'}`}
                placeholder="Marketing channel, referral, etc."
              />
            </div>
            <div>
              <label className="text-sm font-medium">External Reference</label>
              <input
                type="text"
                value={formState.external_ref || ''}
                onChange={(e) => handleChange('external_ref', e.target.value)}
                className={`mt-1 w-full rounded-md border px-3 py-2 ${isDark ? 'border-gray-600 bg-gray-700' : 'border-gray-300'}`}
              />
            </div>
            <div>
              <label className="text-sm font-medium">Avatar URL</label>
              <input
                type="url"
                value={formState.avatar_url || ''}
                onChange={(e) => handleChange('avatar_url', e.target.value)}
                className={`mt-1 w-full rounded-md border px-3 py-2 ${isDark ? 'border-gray-600 bg-gray-700' : 'border-gray-300'}`}
              />
            </div>
            <div>
              <label className="text-sm font-medium">Last Contact Date</label>
              <input
                type="date"
                value={formState.last_contact_date || ''}
                onChange={(e) => handleChange('last_contact_date', e.target.value)}
                className={`mt-1 w-full rounded-md border px-3 py-2 ${isDark ? 'border-gray-600 bg-gray-700' : 'border-gray-300'}`}
              />
            </div>
            <div>
              <label className="text-sm font-medium">Next Follow-up Date</label>
              <input
                type="date"
                value={formState.next_follow_up_date || ''}
                onChange={(e) => handleChange('next_follow_up_date', e.target.value)}
                className={`mt-1 w-full rounded-md border px-3 py-2 ${isDark ? 'border-gray-600 bg-gray-700' : 'border-gray-300'}`}
              />
            </div>
            <div className="md:col-span-2">
              <label className="flex items-center gap-2 text-sm font-medium">
                <input
                  type="checkbox"
                  checked={formState.is_vip}
                  onChange={(e) => handleChange('is_vip', e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                Mark as VIP customer
              </label>
            </div>
          </div>

          <div>
            <label className="text-sm font-medium">Notes</label>
            <textarea
              rows={4}
              value={formState.notes || ''}
              onChange={(e) => handleChange('notes', e.target.value)}
              className={`mt-1 w-full rounded-md border px-3 py-2 ${isDark ? 'border-gray-600 bg-gray-700' : 'border-gray-300'}`}
              placeholder="Track important context, preferences, or next steps"
            />
          </div>
        </div>
        <div
          className={`flex justify-end gap-3 border-t px-6 py-4 ${
            isDark ? 'border-gray-700' : 'border-gray-200'
          }`}
        >
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className={`rounded-lg border px-5 py-2 text-sm font-medium transition-colors ${
              isDark
                ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                : 'border-gray-300 text-gray-700 hover:bg-gray-100'
            } ${isSubmitting ? 'cursor-not-allowed opacity-60' : ''}`}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className={`rounded-lg px-5 py-2 text-sm font-semibold text-white transition-colors ${
              isSubmitting ? 'cursor-not-allowed opacity-60' : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            {isSubmitting ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );

  return createPortal(modalContent, document.body);
}
